/**
 * @file index.tsx
 *
 * @fileoverview Renders an autocomplete that can be used to select elements from.
 */
import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  ComboboxPopover,
} from "@reach/combobox";
import React, { useEffect, useState } from "react";
import Input from "../03-input";
import FormItem from "../11-form-item";
import FormLabel from "../11-form-item/01-form-label";
import styles from "./autocomplete.module.scss";
import { VariantProps, cva } from "class-variance-authority";

const inputIcon = cva([styles.inputIcon], {
  variants: {
    iconPosition: {
      start: styles.iconStart,
      end: styles.iconEnd,
    },
  },
  defaultVariants: {
    iconPosition: "start",
  },
});

const comboboxInput = cva([styles.comboboxInput], {
  variants: {
    iconPosition: {
      start: styles.inputIconStart,
      end: styles.inputIconEnd,
    },
  },
});

export interface AutocompleteProps extends VariantProps<typeof inputIcon> {
  /**
   * The id of the autocomplete.
   */
  id: string;
  /**
   * Input size.
   *
   * @default "40"
   */
  inputSize?: number;
  /**
   * Placeholder text.
   */
  placeholder?: string;
  /**
   * The maximum number of items displayed at once.
   *
   * @default "10"
   */
  maxItemsDisplayed?: number;
  /**
   * The results that match a specific search
   */
  results?: string[];
  /**
   * Handler for a change event in the autocomplete.
   */
  onChange: (event) => void;
  /**
   * When the user makes a selection from the list.
   */
  onSelect?: (value: string) => void;
  /**
   * When enter is triggered with the autocomplete on focus.
   */
  onEnter?: (value: string) => void;
  /**
   * Label for the autocomplete.
   */
  label: string | React.ReactNode;
  /**
   * Initial value for the input.
   */
  value: string;
  /**
   * Elements to display on focus.
   */
  focusElements?: React.ReactNode;
  /**
   * The input icon.
   */
  Icon?: React.ReactNode;
}

const handleKeyDown = (e, onEnter, value) => {
  if (e.key === "Enter" && onEnter) {
    onEnter(value);
    e.preventDefault();
  }
};

export const Autocomplete = ({
  id,
  inputSize = 40,
  placeholder,
  maxItemsDisplayed = 10,
  results,
  onChange,
  onSelect,
  onEnter,
  label,
  value,
  focusElements = null,
  Icon = null,
  iconPosition = "start",
}: AutocompleteProps) => {
  const [showChild, setShowChild] = useState(false);
  const [showFocusElements, setShowFocusElements] = useState(false);
  // Wait until after client-side hydration to show
  useEffect(() => {
    setShowChild(true);
  }, []);

  if (!showChild) {
    // You can show some kind of placeholder UI here.
    // Because combobox does not currently work on the server.
    return (
      <Input
        placeholder={placeholder}
        size={inputSize}
        onChange={onChange}
        value={value}
      />
    );
  }

  return (
    <>
      <FormItem>
        <FormLabel htmlFor={id}>{label}</FormLabel>
        <Combobox
          className={`jcl-autocomplete-combobox ${styles.combobox}`}
          onSelect={onSelect}
          openOnFocus={focusElements !== null}
        >
          <div
            className={`jcl-autocomplete-input-wrapper ${styles.inputWrapper}`}
          >
            <ComboboxInput
              id={id}
              autoComplete="off"
              aria-label={`${label} dropdown input`}
              className={`autocomplete-input ${comboboxInput({
                iconPosition: Icon ? iconPosition : null,
              })}`}
              size={inputSize}
              placeholder={placeholder}
              onChange={onChange}
              onKeyDown={(e) => handleKeyDown(e, onEnter, value)}
              {...(focusElements
                ? { onFocus: (e) => setShowFocusElements(true) }
                : null)}
              value={value}
            />
            {Icon && <div className={inputIcon({ iconPosition })}>{Icon}</div>}
          </div>

          {results && (results.length > 0 || showFocusElements) && (
            <ComboboxPopover
              className={`autocomplete-popup ${styles.comboboxPopover}`}
            >
              <ComboboxList
                className={styles.comboboxList}
                aria-label={`${label} suggestions`}
              >
                {results.slice(0, maxItemsDisplayed).map((result, index) => (
                  <ComboboxOption
                    className={styles.comboboxOption}
                    key={index}
                    value={result}
                  />
                ))}
              </ComboboxList>
              {showFocusElements && <div>{focusElements}</div>}
            </ComboboxPopover>
          )}
        </Combobox>
      </FormItem>
    </>
  );
};
export default Autocomplete;
